import { useEffect, useRef, useState } from 'react';

// mui
import { Box, Button, ToggleButton, ToggleButtonGroup, Typography } from '@mui/material';

import ArrowForwardIcon from '@mui/icons-material/ArrowForward';

// api
import { AdminMenuBar, CommonYesNoDialog, PillTabs } from '../../';
import { AdminConfigUpdate, AdminObjectList, R2Delete, R2Upload } from '../../../api';

// client-shared
import { storage } from '@goldblack/client-storage';
import { ContainerScroll, MarkDown } from '@goldblack/client-comps';

// type → server config key that decides which version users must have agreed to.
const LEGALS = [
    { type: 'privacy', title: 'Privacy Policy', key: 'legalPrivacyVersion', contract: true },
    { type: 'terms', title: 'Terms of Use', key: 'legalTermsVersion', contract: true },
    { type: 'notice', title: 'Notice', key: 'legalNoticeVersion', contract: false },
];

// Legacy markdown carried these lines in the body; they now live in meta.
const stripDate = (t) => t.replace(/\n*\*\*(?:Effective Date|Version):.*\*\*/g, '').trimEnd();

// { meta: { version, effectiveAt | publishedAt }, data: markdown } as a base64 JSON data URL.
// effectiveAt: privacy/terms (shown to users); publishedAt: notice (admin only).
function toDataUrl(rawContent, contract, version, date = new Date().toISOString()) {
    const meta = contract ? { version, effectiveAt: date } : { version, publishedAt: date };
    const json = JSON.stringify({ meta, data: stripDate(rawContent) });
    const encoded = btoa(Array.from(new TextEncoder().encode(json), (b) => String.fromCharCode(b)).join(''));
    return 'data:application/json;base64,' + encoded;
}

// LCS diff of two arrays → [{ t: ' ' | '+' | '-', v }]
function diffSeq(x, y) {
    const dp = Array.from({ length: x.length + 1 }, () => new Array(y.length + 1).fill(0));
    for (let i = x.length - 1; i >= 0; i--)
        for (let j = y.length - 1; j >= 0; j--)
            dp[i][j] = x[i] === y[j] ? dp[i + 1][j + 1] + 1 : Math.max(dp[i + 1][j], dp[i][j + 1]);
    const out = [];
    let i = 0;
    let j = 0;
    while (i < x.length && j < y.length) {
        if (x[i] === y[j]) {
            out.push({ t: ' ', v: x[i] });
            i++;
            j++;
        } else if (dp[i + 1][j] >= dp[i][j + 1]) out.push({ t: '-', v: x[i++] });
        else out.push({ t: '+', v: y[j++] });
    }
    while (i < x.length) out.push({ t: '-', v: x[i++] });
    while (j < y.length) out.push({ t: '+', v: y[j++] });
    return out;
}

// Wraps the word core in U+E000 … U+E001 (read by MarkDown `highlight`). Markup-only tokens (#, -, 1.)
// and links are left alone so the markdown structure survives.
const MARK_OPEN = String.fromCharCode(0xe000);
const MARK_CLOSE = String.fromCharCode(0xe001);
function markToken(tok) {
    if (/^\s*$/.test(tok) || tok.includes('](') || tok.includes('://')) return tok;
    const m = tok.match(/^([^\p{L}\p{N}]*)(.*?)([^\p{L}\p{N}]*)$/su);
    return m[2] ? m[1] + MARK_OPEN + m[2] + MARK_CLOSE + m[3] : tok;
}
const markAll = (text) => text.split(/(\s+)/).map(markToken).join('');

// Word-level diff inside a block of changed lines → [aMarked, bMarked]
function diffWords(a, b) {
    const x = a.split(/(\s+)/);
    const y = b.split(/(\s+)/);
    // ponytail: LCS is O(n*m); a huge rewritten block just gets highlighted as a whole
    if (x.length * y.length > 4e6) return [markAll(a), markAll(b)];
    const ops = diffSeq(x, y);
    const left = ops.filter((o) => o.t !== '+').map((o) => (o.t === '-' ? markToken(o.v) : o.v));
    const right = ops.filter((o) => o.t !== '-').map((o) => (o.t === '+' ? markToken(o.v) : o.v));
    return [left.join(''), right.join('')];
}

// Line diff first (cheap), then word diff inside each changed block → both texts with changed words marked.
function markDiff(a, b) {
    const left = [];
    const right = [];
    let del = [];
    let add = [];
    const flush = () => {
        if (del.length || add.length) {
            const [l, r] = diffWords(del.join('\n'), add.join('\n'));
            if (del.length) left.push(l);
            if (add.length) right.push(r);
        }
        del = [];
        add = [];
    };
    for (const o of diffSeq(a.split('\n'), b.split('\n'))) {
        if (o.t === ' ') {
            flush();
            left.push(o.v);
            right.push(o.v);
        } else (o.t === '-' ? del : add).push(o.v);
    }
    flush();
    return [left.join('\n'), right.join('\n')];
}
function FieldSet({ title, children, sx }) {
    return (
        <Box component='fieldset' sx={{ border: 1, borderColor: 'divider', borderRadius: 1, p: 2, m: 0, ...sx }}>
            <Box component='legend' sx={{ fontSize: 16, color: 'text.primary', px: 0.5 }}>
                {title}
            </Box>
            {children}
        </Box>
    );
}

const MARK_BG = { del: 'rgba(248,81,73,0.35)', add: 'rgba(46,160,67,0.35)' };

// Scrollable markdown body; `mark` ('del' | 'add') highlights the words wrapped by markDiff.
function Content({ text, mark }) {
    return (
        <FieldSet title='Content' sx={{ mt: 2 }}>
            {text != null ? (
                <Box sx={{ maxHeight: '60vh', overflow: 'auto' }}>
                    <MarkDown
                        src={text}
                        highlight={!!mark}
                        sx={mark && { '& mark': { bgcolor: MARK_BG[mark], color: 'inherit' } }}
                    />
                </Box>
            ) : (
                <Typography variant='caption' color='text.disabled'>
                    no file
                </Typography>
            )}
        </FieldSet>
    );
}

const fmtDate = (iso) =>
    new Date(iso).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric', timeZone: 'UTC' });

function LegalCard({ version, controls, live, liveDate, dateLabel, file, liveText, updMode }) {
    const colSx = { minWidth: 0, minInlineSize: 0 };
    const diffed =
        updMode === 'diff' && file && liveText != null ? markDiff(stripDate(liveText), stripDate(file.content)) : null;

    return (
        <Box
            sx={{
                display: 'grid',
                gridTemplateColumns: 'minmax(0, 1fr) auto minmax(0, 1fr)',
                gap: 1,
                alignItems: 'start',
            }}>
            <FieldSet
                title={`Live (version ${live}${liveDate ? `, ${dateLabel} ${fmtDate(liveDate)}` : ''})`}
                sx={colSx}>
                {/* invisible copy of the controls row keeps both Content boxes at the same height */}
                <Box sx={{ visibility: 'hidden' }} aria-hidden>
                    {controls}
                </Box>
                <Content text={diffed ? diffed[0] : liveText} mark={diffed && 'del'} />
            </FieldSet>
            <ArrowForwardIcon
                sx={{
                    fontSize: 16,
                    color: 'text.disabled',
                    alignSelf: 'center',
                    flexShrink: 0,
                }}
            />
            <FieldSet title={`Update (version ${version})`} sx={colSx}>
                {controls}
                <Content text={diffed ? diffed[1] : file?.content} mark={diffed && 'add'} />
            </FieldSet>
        </Box>
    );
}

function LoiseLegal() {
    const [tab, setTab] = useState('privacy');
    const fileInput = useRef(null);
    const [liveTexts, setLiveTexts] = useState({}); // type → fetched live body
    const [files, setFiles] = useState({}); // type → { name, content }
    const [updModes, setUpdModes] = useState({}); // type → 'text' | 'diff'
    const [overwrites, setOverwrites] = useState({}); // type → true: replace body only, keep version
    const [liveDates, setLiveDates] = useState({}); // type → live meta.effectiveAt / publishedAt (ISO)
    const [openOverwrite, setOpenOverwrite] = useState(false);
    const [openReset, setOpenReset] = useState(false);
    const [reload, setReload] = useState(0); // bumped after an overwrite: the version is unchanged, so refetch live manually
    const { loise } = storage.transience.getValue();
    const configs = loise.server.data;

    useEffect(() => {
        AdminObjectList('loise', 'server');
    }, []);

    const liveKey = LEGALS.find((l) => l.type === tab).key;
    const liveVersion = configs?.[liveKey];

    // Show the live body by default; refetch when the tab or the published version changes.
    useEffect(() => {
        if (liveVersion == null) return;
        const setText = (t) => setLiveTexts((prev) => ({ ...prev, [tab]: t }));
        setText('Loading…');
        fetch(`${import.meta.env.VITE_R2_BASE_URL}/legal/${tab}.json?t=${Date.now()}`)
            .then((r) => {
                if (r.status === 404) return null; // not published yet
                return r.ok ? r.json() : Promise.reject(new Error(`HTTP ${r.status}`));
            })
            .then((j) => {
                setLiveDates((prev) => ({ ...prev, [tab]: j?.meta?.effectiveAt ?? j?.meta?.publishedAt }));
                setText(j ? j.data : null);
            })
            .catch((e) => setText(`[error] ${e.message}`));
    }, [tab, liveVersion, reload]);

    if (!configs) return null;

    const legal = LEGALS.find((l) => l.type === tab);
    const live = Number(configs[legal.key]) || 0;
    const file = files[tab];
    const setUpdMode = (m) => setUpdModes((prev) => ({ ...prev, [tab]: m }));
    const setFile = (f) => {
        setFiles((prev) => ({ ...prev, [tab]: f }));
        setUpdMode('text');
    };

    const onFile = (event) => {
        const input = event.target;
        const picked = input.files[0];
        if (!picked) return;
        const reader = new FileReader();
        reader.onload = (e) => setFile({ name: picked.name, content: e.target.result });
        reader.readAsText(picked);
        input.value = '';
    };

    const overwrite = !!overwrites[tab] && live > 0; // nothing to overwrite before the first publish
    const version = overwrite ? live : live + 1;

    // Body first, then the version: users must never be asked to agree to an older text.
    // Overwrite replaces the body only (typo fix): version config and date stay, nobody re-consents.
    const publish = async () => {
        let date;
        if (overwrite) {
            const r = await fetch(`${import.meta.env.VITE_R2_BASE_URL}/legal/${tab}.json?t=${Date.now()}`);
            if (!r.ok) return storage.transience.setState('internal.error', 'No live file to overwrite. Use Update.');
            const meta = (await r.json()).meta;
            date = meta?.effectiveAt ?? meta?.publishedAt;
        }
        const done = () => {
            setFile(null);
            setReload((n) => n + 1);
            storage.transience.setState('internal.success', 'Legal document updated.');
        };
        R2Upload(
            'legal',
            toDataUrl(file.content, legal.contract, version, date),
            () =>
                overwrite
                    ? done()
                    : AdminConfigUpdate('loise', 'update', 'server', null, { [legal.key]: version }, done),
            'loise',
            `legal/${legal.type}.json`,
        );
    };

    // Test-only escape hatch: version first (never leave a version pointing at a missing file), then the file.
    const reset = () =>
        AdminConfigUpdate('loise', 'update', 'server', null, { [legal.key]: 0 }, () =>
            R2Delete('legal', `legal/${legal.type}.json`, 'loise', () => {
                setReload((n) => n + 1);
                storage.transience.setState('internal.success', 'Legal document reset.');
            }),
        );

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
            <PillTabs value={tab} onChange={setTab} tabs={LEGALS.map((l) => ({ value: l.type, label: l.title }))} />
            <input ref={fileInput} hidden type='file' accept='.md,text/markdown,text/plain' onChange={onFile} />
            <CommonYesNoDialog
                open={openOverwrite}
                onClose={() => setOpenOverwrite(false)}
                title='Overwrite Document'
                desc='Overwrite keeps the version: users who already agreed will NOT see the changed text. Continue?'
                onYes={() => {
                    setOpenOverwrite(false);
                    publish();
                }}
                onNo={() => setOpenOverwrite(false)}
            />
            <CommonYesNoDialog
                open={openReset}
                onClose={() => setOpenReset(false)}
                title='Reset Document'
                desc={`Delete the live ${legal.title} file and set its version to 0? This cannot be undone.`}
                onYes={() => {
                    setOpenReset(false);
                    reset();
                }}
                onNo={() => setOpenReset(false)}
            />
            <AdminMenuBar
                apiItems={[
                    {
                        label: 'Update',
                        onClick: () => (overwrite ? setOpenOverwrite(true) : publish()),
                        disabled: !file,
                    },
                    { label: 'Reset', onClick: () => setOpenReset(true), disabled: live === 0 },
                ]}
            />
            <ContainerScroll>
                <Box sx={{ p: 1 }}>
                    <LegalCard
                        version={version}
                        controls={
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <ToggleButtonGroup
                                    exclusive
                                    size='small'
                                    value={overwrite ? 'overwrite' : 'update'}
                                    onChange={(_, v) =>
                                        v && setOverwrites((prev) => ({ ...prev, [tab]: v === 'overwrite' }))
                                    }>
                                    <ToggleButton value='overwrite' disabled={live === 0}>
                                        Overwrite
                                    </ToggleButton>
                                    <ToggleButton value='update'>Update</ToggleButton>
                                </ToggleButtonGroup>
                                <Button size='small' variant='outlined' onClick={() => fileInput.current?.click()}>
                                    Load
                                </Button>
                                <Button
                                    size='small'
                                    variant={updModes[tab] === 'diff' ? 'contained' : 'outlined'}
                                    onClick={() => setUpdMode(updModes[tab] === 'diff' ? 'text' : 'diff')}
                                    disabled={!file || liveTexts[tab] == null}>
                                    Diff
                                </Button>
                            </Box>
                        }
                        live={live}
                        liveDate={liveDates[tab]}
                        dateLabel={legal.contract ? 'effective' : 'published'}
                        file={file}
                        liveText={liveTexts[tab] ?? null}
                        updMode={updModes[tab]}
                    />
                </Box>
            </ContainerScroll>
        </Box>
    );
}
export { LoiseLegal };
